import { memo, useMemo } from "react";
import { Icons } from "../icons/Icons";
import { S, colors, headerTopStyle, settlementValueStyle } from "../styles/ChallanSettlementStyles";
import { Badge, InfoCard, SectionCard } from "../utils/index";
import { getCurrentDate } from "../../../utils/dateFormat";

// ─────────────────────────────────────────────────────────────────────────────

/**
 * @param {{
 *   stockGap: number,
 *   vanStockTransferDetail: Array,
 *   billCount: number,
 *   billAmt: number
 * }} props
 */
const KpiStrip = memo(({ stockGap, vanStockTransferDetail = [], billCount = 0, billAmt = 0 }) => {
    const stockGapColor = stockGap > 0 ? colors.redMid : colors.greenMid;

    // ── KPI 1 : Total loaded ──────────────────────────────────────────────────
    // Sum of secondaryQty across all items
    const totalLoaded = useMemo(
        () => vanStockTransferDetail.reduce((sum, item) => sum + (item.primaryQty ?? 0), 0),
        [vanStockTransferDetail]
    );

    // Unique productId count
    const totalSKUs = useMemo(
        () => new Set(vanStockTransferDetail.map((item) => item.productId)).size,
        [vanStockTransferDetail]
    );

    // ── KPI memoised array ────────────────────────────────────────────────────
    const kpis = useMemo(
        () => [
            {
                label: "Total loaded",
                value: String(totalLoaded),
                sub: `${totalSKUs} SKU${totalSKUs !== 1 ? "s" : ""}`,
                valueStyle: null,
            },
            {
                label: "Billed (sold)",
                value: String(billCount),
                sub: 'Bill Count',
                valueStyle: { color: colors.greenMid },
            },
            {
                label: "Sell amount",
                value: `₹${billAmt}`,
                sub: "Total billed",
                valueStyle: { color: colors.primaryMid },
            },
            {
                label: "Stock gap",
                value: stockGap > 0 ? `${stockGap} units` : "0",
                sub: "Live · enter counts",
                valueStyle: { color: stockGapColor },
            },
        ],
        [totalLoaded, totalSKUs, billCount, billAmt, stockGap, stockGapColor]
    );

    return (
        <div style={S.kpiStrip}>
            {kpis.map(({ label, value, sub, valueStyle }) => (
                <div key={label} style={S.kpi}>
                    <div style={S.kpiLabel}>{label}</div>
                    <div
                        style={valueStyle ? { ...S.kpiValue, ...valueStyle } : S.kpiValue}
                        className="cs-kpi-value"
                    >
                        {value}
                    </div>
                    <div style={S.kpiSub}>{sub}</div>
                </div>
            ))}
        </div>
    );
});

KpiStrip.displayName = "KpiStrip";

// ─────────────────────────────────────────────────────────────────────────────

/**
 * @param {{
 *   stockGap: number,
 *   challanData: object | null
 * }} props
 */
const ChallanHeader = memo(({ stockGap, challanData }) => {

    // ── Header fields ─────────────────────────────────────────────────────────
    const transferNo = challanData?.transferNo ?? "—";
    const challanDate = challanData?.date ?? "—";
    const driverName = challanData?.driverName ?? "—";
    const driverContact = challanData?.driverContactNo ?? "";
    const vehicleNo = challanData?.vehicleNo ?? "—";
    const firmName = challanData?.stockStakeHolderFirmName ?? "—";
    const firmType = challanData?.stockStakeHolderTypeName ?? "";
    const firmPrefix = challanData?.stockStakeHolderTypePrefix ?? "";
    const beatNames = challanData?.beatNames ?? "—";
    const settleDate = getCurrentDate();

    // ── Bill summary ──────────────────────────────────────────────────────────
    const billCount = challanData?.billCount ?? 0;
    const billAmt = challanData?.billAmt.toFixed(2) ?? 0;
    const billQty = challanData?.billQty ?? 0;
    const billSummaryValue = `${billCount} bills · ₹${billAmt} billed · Qty ${billQty}`;

    // ── Outlet sub-line ───────────────────────────────────────────────────────
    const totalOutlet = challanData?.dtoOutletVisitSummary?.totalOutlet ?? 0;
    const totalCover = challanData?.dtoOutletVisitSummary?.totalCover ?? 0;
    const totalMissed = totalOutlet - totalCover;
    const outletSubLine = `${totalOutlet} outlets assigned · ${totalCover} visited · ${totalMissed} missed`;

    // ── Derived display values ────────────────────────────────────────────────
    const firmDisplay = firmPrefix ? `${firmName} (${firmPrefix})` : firmName;
    const sectionTitle = `Challan header — ${transferNo}`;
    const isSettled = challanData?.isSettele === 1;

    // ── vanStockTransferDetail (passed through to KpiStrip) ───────────────────
    const vanStockTransferDetail = challanData?.vanStockTransferDetail ?? [];

    return (
        <SectionCard
            themeKey="doc"
            icon={Icons.doc}
            title={sectionTitle}
            badge={
                <Badge variant={isSettled ? "green" : "amber"} onDark>
                    {isSettled ? "Settled" : "Pending settlement"}
                </Badge>
            }
        >
            <div style={S.sectionBody} className="cs-section-body">

                {/* ── Top meta grid ─────────────────────────────────────── */}
                <div className="cs-header-top" style={headerTopStyle}>
                    <InfoCard label="Transfer no." value={transferNo} />
                    <InfoCard label="Challan date" value={challanDate} />
                    <InfoCard
                        label="Settlement date"
                        value={settleDate}
                        sub="Today — driver returned"
                        valueStyle={settlementValueStyle}
                    />
                    <InfoCard
                        label="Driver name"
                        value={driverName}
                        sub={driverContact ? `Contact: ${driverContact}` : undefined}
                    />
                    <InfoCard label="Vehicle no." value={vehicleNo} />
                    <InfoCard
                        label="Stock holder"
                        value={firmDisplay}
                        sub={firmType || undefined}
                    />
                </div>

                {/* ── Bottom section ────────────────────────────────────── */}
                <div className="cs-header-bottom" style={S.headerBottomGrid}>
                    <div style={S.headerInfoColumn}>
                        <InfoCard
                            label="Beat names"
                            value={beatNames}
                            valueStyle={S.longText}
                        />
                        <InfoCard
                            label="Bill summary"
                            value={billSummaryValue}
                            sub={outletSubLine}
                        />
                    </div>

                    <div style={S.kpiContainer}>
                        <KpiStrip
                            stockGap={stockGap}
                            vanStockTransferDetail={vanStockTransferDetail}
                            billCount={billCount}
                            billAmt={billAmt}
                        />
                    </div>
                </div>

            </div>
        </SectionCard>
    );
});

ChallanHeader.displayName = "ChallanHeader";

export default ChallanHeader;